/**
 * Authenticated Fetch Utility
 *
 * Wraps fetch to automatically include authentication headers for any available tokens.
 * The backend decides which auth mechanism to use for validation.
 *
 * Headers sent (when tokens are available):
 * - Authorization: Bearer <jwt> (when Auth0 token exists)
 * - A3S-Legacy-Authorization: <legacyToken> (when legacy token exists)
 * - a3s-legacy-userid: <userId> (when legacy token exists)
 */

import { useAuthStore } from "../auth-store";
import type { LegacyAuth } from "../types";

export class UnauthorizedError extends Error {
  readonly kind = "http" as const;
  readonly status = 401;

  constructor(message = "Unauthorized") {
    super(message);
    this.name = "UnauthorizedError";
  }
}

export class ForbiddenError extends Error {
  readonly kind = "http" as const;
  readonly status = 403;

  constructor(message = "Forbidden") {
    super(message);
    this.name = "ForbiddenError";
  }
}

export async function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const store = useAuthStore.getState();

  // Get whatever tokens are available
  let accessToken = await store.getAccessToken();
  let legacyAuth = await store.getLegacyAuth();

  const makeRequest = (accessToken: string | null, legacyAuth: LegacyAuth | null): Promise<Response> => {
    const headers = new Headers(options.headers);

    // Include Auth0 token if available
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    // Include legacy headers if available
    if (legacyAuth) {
      headers.set("A3S-Legacy-Authorization", legacyAuth.token);
      headers.set("a3s-legacy-userid", legacyAuth.userId);
    }

    if (!headers.has("Content-Type") && options.body) {
      headers.set("Content-Type", "application/json");
    }

    return fetch(url, { ...options, headers });
  };

  // Must have at least one form of authentication
  if (!accessToken && !legacyAuth) {
    await store.clearAuth();
    throw new UnauthorizedError("No authentication tokens available");
  }

  let response = await makeRequest(accessToken, legacyAuth);

  // Handle 401 - attempt Auth0 token refresh if we have an Auth0 token
  if (response.status === 401 && accessToken) {
    const refreshed = await store.refreshToken();
    if (refreshed) {
      accessToken = await store.getAccessToken();
      if (accessToken) {
        response = await makeRequest(accessToken, legacyAuth);
      }
    }
  }

  if (response.status === 401) {
    throw new UnauthorizedError("Authentication required");
  }

  if (response.status === 403) {
    throw new ForbiddenError("Access denied - insufficient permissions");
  }

  return response;
}
